import { solid, regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { memo } from 'react';
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Models } from '../../api/Models';
import AlidaLoader from '../../components/common/AlidaLoader';
import AssetItem from '../../components/list-items/AssetItem';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions } from '../../utils';
import { Select } from "antd";
import { BdaModel } from '../../models/BdaModel';
import { PagedResults } from '../../models/generics';
import AppBrowsingToolbar, { Filters } from '../../components/common/AppBrowsingToolbar';
import { DefaultOptionType } from 'antd/es/select';
import { EventEmitter } from '../../utils/EventEmitter';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';

const statusOptions = [
  { label: "Virtual", value: "false", icon: solid("lock") },
  { label: "Created", value: "true", icon: regular("check-circle") },
];

const DEFAULT_FILTERS: Filters = {
  'created': {
    placeholder: 'Select status',
    select: true,
    query: 'created',
    type: 'string',
    options: statusOptions,
    customOptions: true,
    renderOption: (opt: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={opt.value} label={opt.label}>
        <div>
          <FontAwesomeIcon icon={opt.icon} fixedWidth />
          <span className="ms-2">{opt.label}</span>
        </div>
      </Select.Option>
    )
  },
  'accessLevel': {
    placeholder: 'Select privacy level',
    select: true,
    query: 'accessLevel',
    type: 'string',
    options: accessLevelOptions
      .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true),
    customOptions: true,
    renderOption: (option: DefaultOptionType, index: number) => (
      <Select.Option key={index} value={option.value} label={option.label}>
        <div>
          <FontAwesomeIcon icon={option.icon} fixedWidth />
          <span className="ms-2">{option.label}</span>
        </div>
      </Select.Option>
    )
  },
  'tags': {
    placeholder: 'Select  tags...',
    select: true,
    query: 'tags',
    type: 'string',
    options: [],
    selectProps: {
      mode: "tags",
    }
  },
  'owned': { checkbox: true, type: 'boolean', query: 'createdBy' },
}

function ModelsList() {
  const [models, setModels] = useState<BdaModel[]>();
  const [totalModels, setTotalModels] = useState<number>();
  const [error, setError] = useState(false);
  const [latestParams, setLatestParams] = useState<URLSearchParams>();

  const [modelToDelete, setModelToDelete] = useState<BdaModel>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));
  const modelsFilters: Filters = useMemo(() => ({
    ...DEFAULT_FILTERS,
    'search': {
      placeholder: `Search ${assetLabels.singular}...`,
      query: 'freeText',
    },
    'owned': {
      ...DEFAULT_FILTERS.owned!,
      label: `My ${assetLabels.plural}`,
    },
  }), [assetLabels]);

  const getModels = useCallback((queryParams?: Record<string, any>) => {
    const params = new URLSearchParams(queryParams || latestParams);
    if (queryParams) setLatestParams(params);

    Models.getModels(params)
      .then(
        ({ data: modelsPages }) => {
          setModels((modelsPages as PagedResults<BdaModel>).content);
          setTotalModels((modelsPages as PagedResults<BdaModel>).pageMetadata.totalElements);
        },
        (err) => {
          showToast('models-error', `Could not get ${assetLabels.plural}`, `${assetLabels.plural} Error`, 'danger');
          setModels([]);
          setError(true);
        }
      );
  }, [showToast, latestParams, assetLabels]);

  const reset = useCallback((params?: Record<string, any>) => {
    setError(false);
    setModels(undefined);
    getModels(params);
  }, [getModels]);

  useEffect(() => {
    EventEmitter.subscribe('browsing:paramsChanged', reset);
    return () => EventEmitter.unsubscribe('browsing:paramsChanged');
  }, [reset]);

  const navigateToCreation = () => navigate('/models/create');
  // const navigateToDetail = (dataset) => () => navigate(`/models/${dataset.id}`);
  const handleClose = () => { setShowDeleteModal(false); setModelToDelete(undefined) };

  // const navigateToEdit = (app) => (evt) => {
  //   evt.stopPropagation();
  //   navigate(`/models/${app.id}/edit`);
  // };

  const openDeleteModal = (model: BdaModel) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setModelToDelete(model);
  }

  const deleteModel = () => {
    showLoader();

    Models.deleteModel(modelToDelete!.id!)
      .then(
        () => {
          hideLoader();
          showToast('model-success', `${assetLabels.singular} deleted successfully`, `${assetLabels.singular} Success`, 'success');
          reset();
          handleClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          showToast('model-error', message || `Could not delete ${assetLabels.singular}`, `${assetLabels.singular} Error`, 'danger');
        }
      )
  }

  const modelDetail = (model: BdaModel) => () => navigate(`/models/${model.id}`);

  const downloadModel = (model: BdaModel) => (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    showLoader();

    Models.downloadModel(model.id!)
      .then(
        ({ data: modelToDownload }) => {
          hideLoader();
          const blob = new Blob([modelToDownload], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${model.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${assetLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const renderModels = () => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    return models!.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {models!.map(model => (
            <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={model.id}>
              <AssetItem
                type="model"
                asset={model}
                onDownload={downloadModel(model)}
                onClick={modelDetail(model)}
                onDelete={openDeleteModal(model)}
              />
            </Col>
          ))}
        </Row>
      )
  }

  const renderAsyncContent = () => {
    return models
      ? renderModels()
      : (
        <div className="d-flex justify-content-center py-5">
          <AlidaLoader size={300} />
        </div>
      );
  }

  return (
    <Container fluid="md" className="mt-3">
      <AppBrowsingToolbar
        title={`${assetLabels.plural} List`}
        actions={
          <Button size="sm" variant="success" onClick={navigateToCreation}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="d-none d-sm-inline ms-2">Register {assetLabels.singular}</span>
          </Button>
        }
        filters={modelsFilters}
        total={totalModels}
      />
      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {assetLabels.singular} <b>{modelToDelete && modelToDelete.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteModel}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(ModelsList);
